"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Bot, Download, Pause, Play, User } from "lucide-react";
import { Button } from "@/components/ui/button";

type TrackKey = "all" | "user" | "ai";

interface AudioPlayerBarProps {
  src: string;
  userSrc?: string;
  aiSrc?: string;
  disabled?: boolean;
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function AudioPlayerBar({ src, userSrc, aiSrc, disabled }: AudioPlayerBarProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [activeTrack, setActiveTrack] = useState<TrackKey>("all");

  const trackUrl = activeTrack === "user" && userSrc
    ? userSrc
    : activeTrack === "ai" && aiSrc
      ? aiSrc
      : src;

  const switchTrack = useCallback((track: TrackKey) => {
    const audio = audioRef.current;
    if (!audio) return;
    const wasPlaying = !audio.paused;
    setActiveTrack(track);
    const newUrl = track === "user" && userSrc
      ? userSrc
      : track === "ai" && aiSrc
        ? aiSrc
        : src;
    audio.src = newUrl;
    audio.currentTime = 0;
    setCurrentTime(0);
    setDuration(0);
    if (wasPlaying) audio.play();
  }, [src, userSrc, aiSrc]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime);
    const onMeta = () => setDuration(audio.duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onEnded = () => { setPlaying(false); setCurrentTime(0); };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("durationchange", onMeta);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("durationchange", onMeta);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  function togglePlay() {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
    } else {
      audio.play();
    }
  }

  function handleSeek(e: React.MouseEvent<HTMLDivElement>) {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setCurrentTime(audio.currentTime);
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const tracks: { key: TrackKey; url: string | undefined; icon: React.ReactNode; label: string }[] = [
    { key: "all", url: src, icon: <Play className="h-3.5 w-3.5" />, label: "全部" },
    ...(userSrc ? [{ key: "user" as const, url: userSrc, icon: <User className="h-3.5 w-3.5" />, label: "用户" }] : []),
    ...(aiSrc ? [{ key: "ai" as const, url: aiSrc, icon: <Bot className="h-3.5 w-3.5" />, label: "AI" }] : []),
  ];

  return (
    <div className="flex flex-1 items-center gap-3">
      <audio ref={audioRef} src={trackUrl} preload="metadata" />

      <Button
        type="button"
        variant="outline"
        size="icon"
        className="h-8 w-8 shrink-0"
        onClick={togglePlay}
        disabled={disabled}
        aria-label={playing ? "暂停" : "播放"}
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </Button>

      <span className="text-xs tabular-nums text-muted-foreground shrink-0 w-10 text-right">
        {formatTime(currentTime)}
      </span>

      <div
        className="relative h-2 flex-1 cursor-pointer rounded-full bg-muted"
        onClick={handleSeek}
      >
        <div
          className="absolute inset-y-0 left-0 rounded-full bg-primary transition-[width] duration-100"
          style={{ width: `${progress}%` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 h-3.5 w-3.5 rounded-full bg-primary border-2 border-background shadow-sm transition-[left] duration-100"
          style={{ left: `calc(${progress}% - 7px)` }}
        />
      </div>

      <span className="text-xs tabular-nums text-muted-foreground shrink-0 w-10">
        {formatTime(duration)}
      </span>

      <div className="flex items-center gap-1 shrink-0">
        {tracks.map(({ key, label }) => (
          <Button
            key={key}
            type="button"
            variant={activeTrack === key ? "default" : "outline"}
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => switchTrack(key)}
            disabled={disabled}
          >
            {label}
          </Button>
        ))}
      </div>

      {disabled ? (
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" disabled aria-label="下载录音">
          <Download className="h-4 w-4" />
        </Button>
      ) : (
        <Button type="button" variant="outline" size="icon" className="h-8 w-8 shrink-0" asChild aria-label="下载录音">
          <a href={trackUrl} download>
            <Download className="h-4 w-4" />
          </a>
        </Button>
      )}
    </div>
  );
}
